/**
 * The arithmetic behind the Shopify overview's charts, drawn twice: the tenant dashboard's
 * Shopify index (`apps/dashboard/src/pages/ShopifyOverview.tsx`) and the embedded admin's Home
 * (`apps/shopify/web/admin/HomeScreen.tsx`). Kept out of both pages so it is tested once and the
 * pages only draw.
 */

/** The fields of `shopify.status`'s `summary.economics` the charts read. */
export interface ShopifyEconomicsView {
  volume: { cents: number };
  projectedMargin: { cents: number; bps: number | null };
  realizedMargin: { cents: number; bps: number | null };
  platformFee: { cents: number };
}

/**
 * The two series colors, checked for color-vision separation and contrast on the light and dark
 * card surfaces. The product payout is the neutral remainder.
 */
export const MARGIN_COLOR = "#7c6ff0";
export const FEE_COLOR = "#d0701a";

/** How each figure on the overview is calculated, shown on hover. Refunded boxes never count. */
export const ECONOMICS_HINTS = {
  volume: "Σ price paid for boxes",
  projectedMargin: "Volume × each box's margin setting",
  realizedMargin: "Paid − item value paid out, on settled boxes. Items at Shopify price, not cost",
  fee: "Volume × your Loot Labs rate",
  billedFee: "Settled boxes × your Loot Labs rate, billed through Shopify app charges",
  projectedPercent: "Projected margin ÷ volume",
  realizedPercent: "Realized margin ÷ settled boxes' paid price",
} as const;

export type VolumeSplitKey = "payout" | "fee" | "margin";

export const VOLUME_SPLIT_HINTS: Record<VolumeSplitKey, string> = {
  payout: "Volume − projected margin",
  fee: ECONOMICS_HINTS.fee,
  margin: "Projected margin − Loot Labs fee",
};

export interface VolumeSplitSegment {
  key: VolumeSplitKey;
  label: string;
  cents: number;
  /** The segment's share of the bar, 0–1. */
  share: number;
}

/**
 * Where the volume goes at the projected margin: the product paid out (volume less the projected
 * margin), the Loot Labs fee, and the margin left after the fee. A fee larger than the projected
 * margin leaves no margin segment; `netMarginCents` still carries the negative figure.
 */
export function volumeSplit(economics: ShopifyEconomicsView): {
  segments: VolumeSplitSegment[];
  netMarginCents: number;
} {
  const payout = Math.max(0, economics.volume.cents - economics.projectedMargin.cents);
  const fee = economics.platformFee.cents;
  const netMarginCents = economics.projectedMargin.cents - fee;
  const parts: [VolumeSplitKey, string, number][] = [
    ["payout", "Product paid out", payout],
    ["fee", "Loot Labs fee", fee],
    ["margin", "Your margin after fee", Math.max(0, netMarginCents)],
  ];
  const total = parts.reduce((sum, [, , cents]) => sum + cents, 0);
  return {
    segments: parts.map(([key, label, cents]) => ({
      key,
      label,
      cents,
      share: total > 0 ? cents / total : 0,
    })),
    netMarginCents,
  };
}

/**
 * The top of the margin chart's scale, in basis points: the larger of the two margins, rounded up
 * to a whole 5%, never under 25% (the highest margin a rule box may keep).
 */
export function marginScaleBps(economics: ShopifyEconomicsView): number {
  const top = Math.max(economics.projectedMargin.bps ?? 0, economics.realizedMargin.bps ?? 0);
  return Math.max(2_500, Math.ceil(top / 500) * 500);
}

/** A margin's bar width on that scale, 0–1; a loss draws no bar. */
export function marginBarShare(bps: number | null, scaleBps: number): number {
  if (bps === null || bps <= 0) return 0;
  return Math.min(1, bps / scaleBps);
}

/** The purchase statuses in the order a unit moves through them, grouped by where it stands. */
export const PURCHASE_STATUS_GROUPS = [
  {
    label: "With the shopper",
    hint: "Payout not final yet",
    statuses: ["processing", "unrevealed", "revealed"],
  },
  { label: "Settled", hint: "Payout final", statuses: ["claimed", "sold", "converted"] },
  { label: "Refunded", hint: "Refunded in Shopify; left out of volume", statuses: ["voided"] },
] as const;

export type PurchaseStatusKey = (typeof PURCHASE_STATUS_GROUPS)[number]["statuses"][number];

export const PURCHASE_STATUS_LABELS: Record<PurchaseStatusKey, string> = {
  processing: "Processing",
  unrevealed: "Unrevealed",
  revealed: "Revealed",
  claimed: "Claimed",
  sold: "Sold back",
  converted: "Converted",
  voided: "Voided",
};

export const PURCHASE_STATUS_HINTS: Record<PurchaseStatusKey, string> = {
  processing: "Order received, customer not matched yet",
  unrevealed: "Bought, not opened",
  revealed: "Opened, not yet claimed or sold back",
  claimed: "Item kept and shipped",
  sold: "Items sold back for store credit",
  converted: "Swapped unopened for store credit",
  voided: "Refunded",
};

export interface PurchaseStatusRow {
  status: PurchaseStatusKey;
  label: string;
  hint: string;
  count: number;
  /** The bar's width against the largest status, 0–1. */
  share: number;
}

/** Every status of every group with its count (0 when the tally has none), in lifecycle order. */
export function purchaseStatusRows(tally: Record<string, number> | undefined) {
  const counts = PURCHASE_STATUS_GROUPS.flatMap((g) => g.statuses).map(
    (status) => tally?.[status] ?? 0,
  );
  const largest = Math.max(0, ...counts);
  return PURCHASE_STATUS_GROUPS.map((group) => ({
    label: group.label,
    hint: group.hint,
    rows: group.statuses.map((status): PurchaseStatusRow => {
      const count = tally?.[status] ?? 0;
      return {
        status,
        label: PURCHASE_STATUS_LABELS[status],
        hint: PURCHASE_STATUS_HINTS[status],
        count,
        share: largest > 0 ? count / largest : 0,
      };
    }),
  }));
}
